import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Button,
    Code,
    Group,
    List,
    Loader,
    Modal,
    Stack,
    Text,
} from '@mantine/core';

import {
    ProviderConnection,
    ProviderDisconnectPreview,
    zoneImpact,
} from '../types';
import {ConnectionStatusBadge} from './ConnectionStatusBadge';

// FNP-1248 — THE DISCONNECT CONFIRMATION.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// The DELETE pattern doc reaches for `modals.openConfirmModal`, and that is the
// right call in the apps it was written from. It is NOT available here:
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` would silently do nothing — the
// worst possible failure for a destructive-action guard, because the button
// would appear inert rather than broken. The zones module hit this exact trap
// and documented it; this follows its resolution.
//
// The doc's SUBSTANCE is kept in full: confirm before destroying, name what is
// being destroyed, red confirm action. Only the mechanism differs.
//
// ⚠️ THE CONFIRM BUTTON IS NOT ARMED UNTIL THE PREVIEW HAS RESOLVED. See
// useProviderDisconnect — the server cannot verify a human saw the consequences,
// so this component is the thing that guarantees they were rendered.

/**
 * The zone-impact line.
 *
 * ⚠️ THIS IS THE MOST DANGEROUS RENDER IN THE MODULE AND IT HAS ITS OWN
 * FUNCTION SO THE BRANCH CANNOT BE INLINED WRONG.
 *
 * `managedZoneCount` is NULL when the connection is broken and could not be
 * asked what it manages. Rendering that null as "0 zones" would tell a tenant
 * that disconnecting costs them nothing when it might cost them every zone they
 * have — the empty-versus-broken confusion at the one moment it is most
 * expensive. `zoneImpact()` reads `zoneCountUnavailable` FIRST and this renders
 * its three cases as three visibly different sentences.
 */
const ZoneImpactLine = ({preview}: { preview: ProviderDisconnectPreview }) => {

    const impact = zoneImpact(preview);

    if (impact.kind === 'UNKNOWN') {
        // FNP-1329 — the caution stays; the DIAGNOSIS has to be earned.
        //
        // "because the connection is not working" used to be baked into this
        // sentence, so it rendered on healthy connections too — directly under
        // a CONNECTED badge. An unknown count means Flexi did not learn the
        // number; on its own it says nothing about the credential. Only the
        // connection's own status can carry that claim.
        const credentialIsKnownBad = preview.status === 'INVALID'
            || preview.status === 'UNREACHABLE';

        return (
            <Alert color={'orange'} variant={'light'} title={I18n.get('Zone count unknown')}>
                {/* ⚠️ UNKNOWN IS NOT ZERO, and the wording has to keep them
                    apart. A broken connection cannot be asked how many zones it
                    manages, so silence here would invite the reader to assume
                    there are none — and disconnect something still serving live
                    DNS. */}
                <Text size={'sm'} data-testid={'zone-impact'}>
                    {credentialIsKnownBad
                        ? I18n.get('Zone count unavailable — the connection is not working. This is not the same as zero.')
                        : I18n.get('Zone count unknown. This is not the same as zero.')}
                </Text>
            </Alert>
        );
    }

    if (impact.kind === 'NONE') {
        return (
            <Text size={'sm'} data-testid={'zone-impact'}>
                {productCopy('{product} is not managing any zones through this connection.')}
            </Text>
        );
    }

    return (
        <Text size={'sm'} fw={500} data-testid={'zone-impact'}>
            {I18n.get('Zones managed through this connection')}: {impact.count}
        </Text>
    );
};

export const DisconnectProviderModal = (
    {
        connection,
        preview,
        opened,
        onClose,
        onConfirm,
        canConfirm,
        isPreviewLoading = false,
        isPreviewError = false,
        onRetryPreview,
        isDisconnecting = false,
    }: {
        connection?: ProviderConnection,
        preview?: ProviderDisconnectPreview,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        // ⚠️ Passed in rather than derived here: the gate belongs to the hook
        // that owns the preview query, so a future caller cannot arm the button
        // by rendering this component without one.
        canConfirm: boolean,
        isPreviewLoading?: boolean,
        isPreviewError?: boolean,
        onRetryPreview?: () => void,
        isDisconnecting?: boolean,
    },
) => {

    if (!connection) {
        return null;
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Disconnect provider')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                {/* NAMES WHAT IS BEING DISCONNECTED, and shows its status — a
                    confirmation reading "disconnect 1 item?" is a rubber stamp.
                    What the tenant may not know is WHICH of their several
                    connections this is. */}
                <Stack gap={4}>
                    <Text size={'sm'}>{I18n.get('You are about to disconnect:')}</Text>
                    <Group gap={'xs'}>
                        <Code>{connection.displayName}</Code>
                        <ConnectionStatusBadge connection={connection}/>
                    </Group>
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Provider')}: <Code>{connection.providerId}</Code>
                    </Text>
                </Stack>

                {/* ─── THE PREVIEW ─── */}

                {isPreviewLoading && (
                    <Group gap={'xs'}>
                        <Loader size={'xs'}/>
                        <Text size={'sm'} c={'dimmed'}>
                            {I18n.get('Checking what this would affect…')}
                        </Text>
                    </Group>
                )}

                {/* ⚠️ A FAILED PREVIEW BLOCKS THE DISCONNECT rather than falling
                    back to a generic "are you sure?". The whole point of the
                    preview is that the cost is not visible from the row; if it
                    could not be read, nobody can consent to it. */}
                {isPreviewError && (
                    <Alert color={'red'} variant={'light'} title={I18n.get('Could not check the impact')}>
                        <Group justify={'space-between'} align={'center'}>
                            <Text size={'sm'}>
                                {productCopy('{product} could not read what disconnecting would affect, so this cannot be confirmed yet.')}
                            </Text>
                            {onRetryPreview && (
                                <Button variant={'outline'} size={'xs'} onClick={onRetryPreview}>
                                    {I18n.get('Retry')}
                                </Button>
                            )}
                        </Group>
                    </Alert>
                )}

                {preview && (
                    <Stack gap={'md'}>

                        <ZoneImpactLine preview={preview}/>

                        {/* The API's own consequence sentences, VERBATIM and in
                            the order given — the schema states they are sorted
                            most severe first and are written to be read by a
                            human deciding whether to proceed. Re-wording or
                            re-ordering them here would discard that. */}
                        {preview.consequences.length > 0 && (
                            <Stack gap={4}>
                                <Text size={'sm'} fw={500}>
                                    {I18n.get('What happens when you disconnect')}
                                </Text>
                                <List size={'sm'} spacing={4} data-testid={'consequences'}>
                                    {preview.consequences.map((consequence, index) => (
                                        <List.Item key={index}>{consequence}</List.Item>
                                    ))}
                                </List>
                            </Stack>
                        )}

                        <Text size={'xs'} c={'dimmed'}>
                            {I18n.get('The stored credential is deleted. Reconnecting needs the credential again, and a new external ID.')}
                        </Text>
                    </Stack>
                )}

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDisconnecting}>
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDisconnecting}
                        // ⚠️ Disabled until the preview resolved. This is the
                        // client-side half of the schema's "call the preview
                        // first and show the caller what it says".
                        disabled={!canConfirm}
                    >
                        {I18n.get('Disconnect')}
                    </Button>
                </Group>

            </Stack>
        </Modal>
    );
};
